/**
 * Simple FTD Field Data Configuration
 * Just the data - no complex logic
 */

export interface FieldData {
  type: "User input" | "Parameter" | "Formula";
  glossaryText?: string;
  formula?: string;
  affectedFields?: string;
  formulaFields?: string; // Fields used in this formula (for highlighting)
}

/**
 * Simple lookup object for FTD fields
 * Key = Field name exactly as it appears in your table
 */
export const FTD_FIELDS: Record<string, FieldData> = {
  Growth: {
    type: "User input",
    glossaryText: "By Default, Average of the last 6 full months",
    affectedFields: "FTD",
  },

  "Active Users": {
    type: "Formula",
    formula: "Old Users + Acquired Users",
    affectedFields: "",
    formulaFields: "Old Users, Acquired Users",
  },

  "Old Users": {
    type: "Formula",
    formula: "(Last Full Month Active Users) * (1 - Churn Rate)",
    affectedFields: "Active Users, FTD",
    formulaFields: "Churn Rate, Active Users",
  },

  "Acquired Users": {
    type: "Formula",
    formula: "FTD * (Acquired Users / FTD Ratio)",
    affectedFields: "Active Users",
    formulaFields: "FTD, Acquired Users / FTD Ratio",
  },

  "Churn Rate": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Old Users, FTD",
  },

  FTD: {
    type: "Formula",
    formula:
      "(((1 / (1 - Churn Rate)) - 1) * Old Users + (Last Full Month Active Users * Growth)) / Acquired Users to FTD Ratio",
    affectedFields: "Acquired Users, RS PV, CPA Income, Clicks, CAC",
    formulaFields:
      "Churn Rate, Old Users, Active Users, Growth, Acquired Users / FTD Ratio",
  },

  "Acquired Users / FTD Ratio": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Acquired Users, FTD",
  },

  "Average Net Revenue Per Active User": {
    type: "User input",
    glossaryText: "By Default, Average of the last 6 full months",
    affectedFields: "Net Rev",
  },

  "Estimated Net Revenue": {
    type: "Formula",
    formula:
      "Average Net Revenue Per Active User * Active Users * [RS BR to NG Active Ratio]",
    affectedFields: "RS",
    formulaFields:
      "Average Net Revenue Per Active User, Active Users, RS BR to NG Active Ratio",
  },

  "Average RS %": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "RS",
  },

  "RS BR to NG Active Ratio": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Net Rev",
  },

  "Estimated Revenue Share": {
    type: "Formula",
    formula: "Average Net Revenue * Average RS percentage",
    affectedFields: "RS PV, EPC RS",
    formulaFields: "Estimated Net Revenue, Average RS %",
  },

  "RS PV": {
    type: "Formula",
    formula: "Estimated RS / FTD",
    affectedFields: "",
    formulaFields: "Estimated Revenue Share, FTD",
  },

  "CPA PV": {
    type: "User input",
    glossaryText: "By Default, Last full month value",
    affectedFields: "est. CPA Income",
  },

  "Estimated CPA Income": {
    type: "Formula",
    formula: "CPA PV * FTD",
    affectedFields: "Total Income, EPC CPA",
    formulaFields: "CPA PV, FTD",
  },

  "Total Income": {
    type: "Formula",
    formula: "Estimated RevShare + Estimated CPA Income",
    affectedFields: "Spend",
    formulaFields: "Estimated Revenue Share, Estimated CPA Income",
  },

  "Conversion %": {
    type: "User input",
    glossaryText: "By Default, Average of the last 6 full months",
    affectedFields: "Clicks",
  },

  Clicks: {
    type: "Formula",
    formula: "FTD / Conversion Percentage",
    affectedFields: "EPC CPA, EPC RS, CPC",
    formulaFields: "FTD, Conversion %",
  },

  "EPC CPA": {
    type: "Formula",
    formula: "Estimated CPA Income / Clicks",
    affectedFields: "EPC",
    formulaFields: "Estimated CPA Income, Clicks",
  },

  "EPC RS": {
    type: "Formula",
    formula: "Estimated Revenue Share / Clicks",
    affectedFields: "EPC",
    formulaFields: "Estimated Revenue Share, Clicks",
  },

  EPC: {
    type: "Formula",
    formula: "EPC CPA + EPC RS",
    affectedFields: "",
    formulaFields: "EPC CPA, EPC RS",
  },

  CPC: {
    type: "Formula",
    formula: "Spend / Clicks",
    affectedFields: "",
    formulaFields: "Spend, Clicks",
  },

  CAC: {
    type: "Formula",
    formula: "Spend / FTD",
    affectedFields: "",
    formulaFields: "Spend, FTD",
  },

  Spend: {
    type: "Formula",
    formula: "Total Income - Profit",
    affectedFields: "CPC, CAC",
    formulaFields: "Total Income, Profit",
  },

  Profit: {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Spend",
  },
};

/**
 * Field Names Constants
 * Use these constants throughout your application for consistency
 */
export const FIELD_NAMES = {
  GROWTH: "Growth",
  AVG_NET_REV: "Average Net Revenue Per Active User",
  CPA_PV: "CPA PV",
  CVR: "Conversion %",
  PROFIT: "Profit",
  ACTIVE_USERS: "Active Users",
  OLD_USERS: "Old Users",
  ACQUIRED_USERS: "Acquired Users",
  FTD: "FTD",
  NET_REVENUE: "Estimated Net Revenue",
  RS_PV: "RS PV",
  CPA_INCOME: "Estimated CPA Income",
  TOTAL_INCOME: "Total Income",
  CLICKS: "Clicks",
  EPC_CPA: "EPC CPA",
  EPC_RS: "EPC RS",
  EPC: "EPC",
  CPC: "CPC",
  CAC: "CAC",
  SPEND: "Spend",
  AVG_RS_P: "Average RS %",
  CHURN_RATE: "Churn Rate",
  RS_BR_NG: "RS BR to NG Active Ratio",
  ACQUIRED_TO_FTD: "Acquired Users / FTD Ratio",
  ESTIMATED_REVENUE_SHARE: "Estimated Revenue Share",
  NRPU: "NRPU",
} as const;

/**
 * Simple helper to get field info
 */
export const getFieldInfo = (fieldName: string) => {
  return FTD_FIELDS[fieldName] || null;
};
